import React from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND, BRAND_LIGHT, getThemeColors } from '../../../lib/theme';
import { usePbDashboardConfig, type PbDashboardWidgetId, type WidgetSize, type DashboardAreaScope } from '../../../lib/usePbDashboardConfig';
import { ShowAllLink } from './WidgetCard';

/* ─── ExpandedCard — echtes Tab-Element im Dashboard eingebettet ─────────── */

export function ExpandedCard({ dark, title, onShowAll, showAllLabel, children }: {
  dark: boolean; title: string; onShowAll: (e: React.MouseEvent) => void; showAllLabel: string; children: React.ReactNode;
}) {
  const tc = getThemeColors(dark);
  return (
    <div style={{ background: tc.card, border: `1px solid ${tc.border}`, borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: `1px solid ${tc.border}` }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: tc.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{title}</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 10, background: BRAND_LIGHT, color: BRAND, borderRadius: 999, padding: '1px 8px', fontWeight: 600 }}>erweitert</span>
          <ShowAllLink label={showAllLabel} onClick={onShowAll} />
        </div>
      </div>
      <div style={{ padding: '16px', flex: 1 }}>
        {children}
      </div>
    </div>
  );
}

/* ─── Einstellungs-Panel ─────────────────────────────────────────────────── */

const WIDGET_DEFS: { id: PbDashboardWidgetId; labelKey: string }[] = [
  { id: 'betreuungen',  labelKey: 'pflege.detail.dashboard.betreuungen' },
  { id: 'aufgaben',     labelKey: 'pflege.detail.dashboard.aufgaben' },
  { id: 'notizen',      labelKey: 'pflege.detail.dashboard.notizen' },
  { id: 'nachrichten',  labelKey: 'pflege.detail.dashboard.nachrichten' },
  { id: 'dokumente',    labelKey: 'pflege.detail.dashboard.dokumente' },
  { id: 'medien',       labelKey: 'pflege.detail.dashboard.medien' },
  { id: 'bewertungen',  labelKey: 'pflege.detail.dashboard.bewertungen' },
  { id: 'matching',     labelKey: 'pflege.detail.dashboard.matching' },
  { id: 'gespraechsprotokolle', labelKey: 'pflege.detail.dashboard.gespraechsprotokolle' },
];

export function SettingsPanel({
  dark,
  widgetSettings,
  setWidgetSettings,
  dragEnabled,
  setDragEnabled,
  resizeEnabled,
  setResizeEnabled,
  onClose,
  areaScope,
}: {
  /** Paket 2310e: Auswahl "Nur für mich" / "Standard für alle" + Zurücksetzen. */
  areaScope?: DashboardAreaScope;
  dark: boolean;
  widgetSettings: ReturnType<ReturnType<typeof usePbDashboardConfig>['widgetSettings']> extends infer T ? (id: PbDashboardWidgetId) => T : never;
  setWidgetSettings: (id: PbDashboardWidgetId, patch: Partial<{ visible: boolean; mode: 'compact' | 'expanded' }>) => void;
  dragEnabled: boolean;
  setDragEnabled: (enabled: boolean) => void;
  resizeEnabled: boolean;
  setResizeEnabled: (enabled: boolean) => void;
  onClose: () => void;
}) {
  const tc = getThemeColors(dark);
  const { t } = useTranslation();
  return (
    <div style={{
      background: tc.card, border: `1px solid ${tc.border}`, borderRadius: 14, padding: 20,
      boxShadow: dark ? '0 8px 30px rgba(0,0,0,0.5)' : '0 8px 30px rgba(16,24,40,0.12)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: tc.heading }}>{t('pflege.detail.dashboard.settings_title')}</span>
        <button onClick={onClose} style={{ background: 'none', border: `1px solid ${tc.border}`, borderRadius: 7, padding: '4px 10px', cursor: 'pointer', color: tc.muted, fontSize: 12 }}>{t('common.action.close')}</button>
      </div>

      {areaScope && <AreaScopeBar dark={dark} areaScope={areaScope} />}

      {/* Drag toggle */}
      <div style={{ marginBottom: 14, padding: '10px 14px', background: dragEnabled ? BRAND_LIGHT : tc.hover, borderRadius: 9, border: `1px solid ${dragEnabled ? BRAND : tc.border}` }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={dragEnabled}
            onChange={e => setDragEnabled(e.target.checked)}
            style={{ cursor: 'pointer', accentColor: BRAND, width: 16, height: 16 }}
          />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: dragEnabled ? BRAND : tc.heading }}>
              {t('pflege.detail.dashboard.drag_toggle_label')}
            </div>
            <div style={{ fontSize: 11, color: tc.muted, marginTop: 1 }}>
              {t('pflege.detail.dashboard.drag_toggle_hint')}
            </div>
          </div>
        </label>
      </div>

      {/* Resize toggle */}
      <div style={{ marginBottom: 14, padding: '10px 14px', background: resizeEnabled ? BRAND_LIGHT : tc.hover, borderRadius: 9, border: `1px solid ${resizeEnabled ? BRAND : tc.border}` }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={resizeEnabled}
            onChange={e => setResizeEnabled(e.target.checked)}
            style={{ cursor: 'pointer', accentColor: BRAND, width: 16, height: 16 }}
          />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: resizeEnabled ? BRAND : tc.heading }}>
              {t('pflege.detail.dashboard.resize_toggle_label')}
            </div>
            <div style={{ fontSize: 11, color: tc.muted, marginTop: 1 }}>
              {t('pflege.detail.dashboard.resize_toggle_hint')}
            </div>
          </div>
        </label>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {WIDGET_DEFS.map(({ id, labelKey }) => {
          const ws = widgetSettings(id);
          return (
            <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: tc.hover, borderRadius: 9, border: `1px solid ${tc.border}` }}>
              {/* visible toggle */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', flex: '0 0 auto' }}>
                <input
                  type="checkbox"
                  checked={ws.visible}
                  onChange={e => setWidgetSettings(id, { visible: e.target.checked })}
                  style={{ cursor: 'pointer', accentColor: BRAND, width: 15, height: 15 }}
                />
              </label>
              <span style={{ fontSize: 13, fontWeight: 500, color: ws.visible ? tc.heading : tc.muted, flex: 1, textDecoration: ws.visible ? 'none' : 'line-through' }}>
                {t(labelKey)}
              </span>
              {/* mode toggle */}
              {ws.visible && (
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                  {(['compact', 'expanded'] as const).map(mode => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setWidgetSettings(id, { mode })}
                      style={{
                        fontSize: 11, fontWeight: 600, borderRadius: 6,
                        padding: '3px 10px', cursor: 'pointer',
                        border: `1px solid ${ws.mode === mode ? BRAND : tc.border}`,
                        background: ws.mode === mode ? BRAND_LIGHT : 'transparent',
                        color: ws.mode === mode ? BRAND : tc.muted,
                      }}
                    >
                      {mode === 'compact' ? t('pflege.detail.dashboard.mode_compact') : t('pflege.detail.dashboard.mode_expanded')}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 12, fontSize: 11.5, color: tc.muted, fontStyle: 'italic' }}>
        {t('pflege.detail.dashboard.settings_hint')}
      </div>
    </div>
  );
}

/* ─── AreaScopeBar — Speicherziel (nur ich / Standard für alle) + Zurücksetzen ── */

function AreaScopeBar({ dark, areaScope }: { dark: boolean; areaScope: DashboardAreaScope }) {
  const tc = getThemeColors(dark);
  const { t } = useTranslation();
  const { source, scope, setScope, canSetDefault, resetToDefault, clearDefault } = areaScope;
  const [busy, setBusy] = React.useState(false);
  const run = async (fn: () => Promise<void>) => { setBusy(true); try { await fn(); } finally { setBusy(false); } };
  const btn: React.CSSProperties = {
    background: 'none', border: `1px solid ${tc.border}`, borderRadius: 7, padding: '4px 10px',
    cursor: busy ? 'default' : 'pointer', color: tc.text, fontSize: 12, opacity: busy ? 0.6 : 1,
  };
  return (
    <div data-testid="dashboard-area-scope" style={{ marginBottom: 14, padding: '10px 14px', background: tc.hover, borderRadius: 9, border: `1px solid ${tc.border}` }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: tc.muted }}>{t('pflege.detail.dashboard.scope_label')}</span>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', color: tc.heading }}>
          <input type="radio" name="dashboard-scope" checked={scope === 'user'} onChange={() => setScope('user')} style={{ accentColor: BRAND }} />
          {t('pflege.detail.dashboard.scope_user')}
        </label>
        {canSetDefault && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', color: tc.heading }}>
            <input type="radio" name="dashboard-scope" checked={scope === 'default'} onChange={() => setScope('default')} style={{ accentColor: BRAND }} />
            {t('pflege.detail.dashboard.scope_default')}
          </label>
        )}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginTop: 8 }}>
        <span style={{ fontSize: 11.5, color: tc.muted }}>
          {scope === 'default' ? t('pflege.detail.dashboard.scope_default_hint') : t(`pflege.detail.dashboard.source_${source}`)}
        </span>
        {scope === 'user' && source === 'user' && (
          <button type="button" disabled={busy} onClick={() => run(resetToDefault)} style={btn}>
            {t('pflege.detail.dashboard.reset_to_default')}
          </button>
        )}
        {scope === 'default' && (
          <button type="button" disabled={busy} onClick={() => run(clearDefault)} style={btn}>
            {t('pflege.detail.dashboard.clear_default')}
          </button>
        )}
      </div>
    </div>
  );
}

/* ─── ResizeControls — +/- Steuerung für Breite/Höhe ─────────────────────── */

const RESIZE_MAX = 4;

export function ResizeControls({
  dark, size, onChange,
}: {
  dark: boolean;
  size: WidgetSize;
  onChange: (size: WidgetSize) => void;
}) {
  const { t } = useTranslation();
  const tc = getThemeColors(dark);
  const clamp = (n: number) => Math.min(RESIZE_MAX, Math.max(1, n));

  const stepper = (
    label: string,
    axis: 'w' | 'h',
    value: number,
  ) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <span style={{ fontSize: 10.5, fontWeight: 700, color: tc.muted }}>{label}</span>
      <button
        type="button"
        aria-label={`${label} −`}
        onClick={(e) => { e.stopPropagation(); onChange({ ...size, [axis]: clamp(value - 1) }); }}
        disabled={value <= 1}
        style={{ width: 22, height: 22, borderRadius: 6, border: `1px solid ${tc.border}`, background: tc.card, color: tc.text, cursor: value <= 1 ? 'default' : 'pointer', fontSize: 13, lineHeight: 1, opacity: value <= 1 ? 0.4 : 1 }}
      >−</button>
      <span style={{ fontSize: 12, fontWeight: 700, color: tc.heading, minWidth: 12, textAlign: 'center' }}>{value}</span>
      <button
        type="button"
        aria-label={`${label} +`}
        onClick={(e) => { e.stopPropagation(); onChange({ ...size, [axis]: clamp(value + 1) }); }}
        disabled={value >= RESIZE_MAX}
        style={{ width: 22, height: 22, borderRadius: 6, border: `1px solid ${tc.border}`, background: tc.card, color: tc.text, cursor: value >= RESIZE_MAX ? 'default' : 'pointer', fontSize: 13, lineHeight: 1, opacity: value >= RESIZE_MAX ? 0.4 : 1 }}
      >+</button>
    </div>
  );

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
        padding: '6px 10px', marginBottom: 6,
        background: BRAND_LIGHT, border: `1px solid ${BRAND}`, borderRadius: 8,
      }}
    >
      <span style={{ fontSize: 10.5, fontWeight: 700, color: BRAND, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {t('pflege.detail.dashboard.resize_size_label')}
      </span>
      {stepper(t('pflege.detail.dashboard.resize_width'), 'w', size.w)}
      {stepper(t('pflege.detail.dashboard.resize_height'), 'h', size.h)}
    </div>
  );
}
